<template>
  <div class="group relative h-full f-col overflow-hidden" :class="bgClass">
    <base-button
      v-if="quickshop"
      :aria-describedby="product.id"
      :aria-label="$t.add"
      class="absolute right-0 top-0 z-3 p-1 group-@hover:op-100 duration lg:op-0 group-focus-within:lg:op-100"
      data-test-id="product-card-add"
      style="margin-right: 0.125rem"
      @click="PanelQuickshop.open({ product, closeOnAdd: closeQuickshopOnAdd, gtmConfig })"
    >
      <span class="m-1 block rounded-xs duration">
        <vf-icon name="quickshop" size="md" />
      </span>
    </base-button>
    <base-picture
      v-bind="size"
      :alt="image.alt"
      class="w-full overflow-hidden"
      :props-img="{ class: 'duration-800 group-hover:scale-105' }"
      :src="getResponsiveImageTransformations(image.src, {
        ...size,
        ...transformations,
      })"
    />
    <div v-style:c="textColor" class="px-2 pb-4 pt-3 lg:p-4">
      <base-link :id class="subtitle-6 line-clamp-4 w-fit" :to="url">
        {{ title }}
        <span class="absolute-0" />
      </base-link>
      <product-pricing
        class="body-5"
        compact-omnibus
        :currency
        :price
        :show-discount-percentage="$feature.showDiscountPercentageOnCatalog"
        show-dynamic-pricing-on-plp
        style="margin-top: 0.375rem"
      />
    </div>

    <panel-quickshop v-slot="props" class-wrapper="!p-0" :size="{ sm: 'lg', lg: 'full' }" :title="$t.size">
      <dialog-quickshop v-bind="props" :product-id="product.id" />
    </panel-quickshop>
  </div>
</template>

<script lang="ts" setup>
import type { RecommendationStyle } from '#content/contexts/recommendation'
import type { ProductVariants } from '#root/api/clients/product/data-contracts'
import type { ProductContext } from '#types/gtm'
import type { ImageItem } from '#types/media'
import type { MonetateRating } from '#types/p13n'
import type { PriceFull } from '#types/product'
import { RecommendationStyleContextKey } from '#content/contexts/recommendation'

const { currency, id, image, price, title, url, quickshop = true } = defineProps<{
  /**
   * Product name or title
   */
  title: string
  /**
   * Path to product detail page
   */
  url: string
  /**
   * Default product image
   */
  image: ImageItem
  /**
   * Product rating data
   */
  rating?: MonetateRating
  /**
   * Product id
   */
  id: string
  /**
   * Product price currency
   */
  currency: string
  /**
   * Product price data
   */
  price: PriceFull
  /**
   * Quickshop Toggle
   */
  quickshop?: boolean
  gtmConfig: ProductContext
}>()

const route = useRoute()

const product = computed(() => ({
  id,
  name: title,
  url,
  price,
  currency,
  attributes: [],
  variants: {} as ProductVariants
}))

const closeQuickshopOnAdd = route.name?.toString().includes('cart')

const {
  presets: { size, transformations }
} = useAppConfig().components.cms.productRecommendations

const PanelQuickshop = createPanel('quickshop')

const { textColor, whiteBackground } = inject(RecommendationStyleContextKey, {} as RecommendationStyle)

const bgClass = whiteBackground === false ? '' : 'bg-white'
</script>
